import {ProvidersDentalGuide} from 'domain/usecases';
import {PlanTypeEnum} from 'presentation/constants/bookingTypesEnum';
import {useGuideStore} from 'presentation/modules/guide/shared/store/guideStore';
import {SortOrderType} from 'presentation/modules/guide/shared/store/guideStore/types';
import {matchesProviderName} from 'presentation/modules/guide/shared/utils/matchesProviderName';
import {useQueryParams} from 'presentation/pages/private';
import {useMemo} from 'react';
import {useOdontoProvidersQuery} from '../../queries/useOdontoProvidersQuery';
import {UseOdontoProvidersQueryParams} from '../../queries/useOdontoProvidersQuery/types';

export function useFilteredOdontoProviders(params: UseOdontoProvidersQueryParams) {
	const {SEARCH_PARAMS} = useQueryParams();
	const {districtNames, orderBy, query} = SEARCH_PARAMS;
	const searchByProviderName = useGuideStore(state => state.searchByProviderName);

	const {
		data: allProviders = [],
		refetch: refetchProvidersQuery,
		isFetching,
		isError: providersError,
	} = useOdontoProvidersQuery(params);

	const isError = providersError;

	const providerName = searchByProviderName[PlanTypeEnum.ODONTO] || query;
	const currentSortOrder = orderBy as SortOrderType;

	const sortProviders = (providers: ProvidersDentalGuide[]): ProvidersDentalGuide[] => {
		if (!providers?.length) return [];

		if (!currentSortOrder || currentSortOrder === SortOrderType.NONE) {
			return [...providers].sort(
				(a, b) => (a?.distancia ?? Infinity) - (b?.distancia ?? Infinity),
			);
		}

		return [...providers].sort((a, b) => {
			const nameA = (a?.nomeFantasia || a?.nome || '').trim();
			const nameB = (b?.nomeFantasia || b?.nome || '').trim();

			const compare = nameA.localeCompare(nameB, 'pt-BR', {sensitivity: 'base'});
			return currentSortOrder === SortOrderType.DESC ? -compare : compare;
		});
	};

	const filteredProviders = useMemo((): ProvidersDentalGuide[] => {
		const filterList = (list: ProvidersDentalGuide[]) => {
			let result = list;
			if (providerName) {
				result = result.filter(
					provider =>
						matchesProviderName(provider?.nome, providerName) ||
						matchesProviderName(provider?.nomeFantasia, providerName),
				);
			}
			return sortProviders(result);
		};

		return filterList(allProviders as ProvidersDentalGuide[]);
	}, [allProviders, providerName, currentSortOrder, districtNames]);

	return {
		data: filteredProviders,
		allProviders,
		isFetching,
		isError,
		refetchProvidersQuery,
	};
}
